import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { usePopoverPositioning } from '@/feature/tags/hooks'
import { useGlobalOrgTags } from '@/feature/tags/hooks/useGlobalOrgTags'
import { useTagsStore } from '@/feature/tags/store'

import { TooltipContainerProvider } from '@/lib/core/components/Tooltip'
import { useEscapeKey } from '@/lib/core/hooks/useEscapeKey'

import { AssetTag, ModalOpenVariant } from '../TableHeaderTags/TableHeaderTags.types'
import { CreateTagScreen } from './CreateTagScreen'
import { EditTagScreen } from './EditTagScreen'
import { TagsListView } from './TagsListView'
import { TagsPopoutView } from './TagsPopout.types'
import styles from './TagsPopout.module.css'

export const TagsPopout = () => {
  const {
    isPopoutOpen,
    closePopout,
    openModal,
    popoverPosition,
    isTagsUpdating,
    globalOrgTags,
    activeAssetData,
  } = useTagsStore()

  // Use the global org tags hook to ensure store is synced with API
  useGlobalOrgTags()

  // State management for the popout views
  const [currentView, setCurrentView] = useState<TagsPopoutView>(TagsPopoutView.LIST)
  const [searchTerm, setSearchTerm] = useState('')
  const [editingTag, setEditingTag] = useState<AssetTag | null>(null)

  const searchFilteredTags = useMemo(() => {
    let filteredTags = globalOrgTags

    // Apply search filter if search term exists
    if (searchTerm.trim()) {
      filteredTags = globalOrgTags.filter((tag) =>
        tag.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Sort tags: selected ones first, then unselected ones
    const currentAssetTagIds = new Set(activeAssetData?.tags?.map((tag) => tag.id) || [])

    return filteredTags.sort((a, b) => {
      const aIsSelected = currentAssetTagIds.has(a.id)
      const bIsSelected = currentAssetTagIds.has(b.id)

      // If both are selected or both are unselected, sort alphabetically by name
      if (aIsSelected === bIsSelected) {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      }

      // Selected tags come first (return -1 if a is selected and b is not)
      return aIsSelected ? -1 : 1
    })
  }, [globalOrgTags, searchTerm, activeAssetData?.tags])

  const dialogRef = useRef<HTMLDialogElement>(null)
  const [popoverDimensions, setPopoverDimensions] = useState({ width: 300, height: 400 })
  const { applyPosition } = usePopoverPositioning(popoverDimensions, { margin: 10 })

  // Position the popover with viewport constraints
  const positionPopover = useCallback(() => {
    if (!dialogRef.current || !popoverPosition) return

    // Get actual dimensions of the popover
    const { offsetWidth, offsetHeight } = dialogRef.current

    // Update dimensions if they've changed
    if (offsetWidth !== popoverDimensions.width || offsetHeight !== popoverDimensions.height) {
      setPopoverDimensions({ width: offsetWidth, height: offsetHeight })
    }

    applyPosition(dialogRef.current, popoverPosition, { width: offsetWidth, height: offsetHeight })
  }, [popoverPosition, applyPosition, popoverDimensions])

  useEffect(() => {
    if (isPopoutOpen && dialogRef.current) {
      positionPopover()
      dialogRef.current.showPopover()
    } else if (dialogRef.current) {
      dialogRef.current.hidePopover()
    }
  }, [isPopoutOpen, positionPopover])

  // All this logic is for handling the clicks outside the popover to close it
  // Track where the mouse was initially pressed to prevent closing on drag-release
  const mouseDownTargetRef = useRef<EventTarget | null>(null)
  // Handle mouse down to track where the interaction started
  const handleDocumentMouseDown = useCallback((e: MouseEvent) => {
    mouseDownTargetRef.current = e.target
  }, [])
  // Create a stable reference to the click handler
  const handleDocumentClick = useCallback(
    (e: MouseEvent) => {
      // Only close if both mousedown and mouseup/click happened outside the popover
      const clickTarget = e.target as Node
      const mouseDownTarget = mouseDownTargetRef.current as Node

      if (
        dialogRef.current &&
        !dialogRef.current.contains(clickTarget) &&
        mouseDownTarget &&
        !dialogRef.current.contains(mouseDownTarget)
      ) {
        closePopout()
      }

      // Reset the mousedown target
      mouseDownTargetRef.current = null
    },
    [closePopout],
  )

  // Add event listener for the popover closing
  useEffect(() => {
    const handlePopoverClose = () => {
      // Check if the popover is still open and we're not in the middle of a tag operation
      if (dialogRef.current && !dialogRef.current.matches(':popover-open')) {
        closePopout()
      }
    }

    // Add event listeners for mouse interactions on the document
    document.addEventListener('mousedown', handleDocumentMouseDown)
    document.addEventListener('click', handleDocumentClick)

    // Add event listener to the dialog element for toggle events
    const dialogElement = dialogRef.current
    if (dialogElement) {
      dialogElement.addEventListener('toggle', handlePopoverClose)
    }

    // Clean up the event listeners
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown)
      document.removeEventListener('click', handleDocumentClick)

      if (dialogElement) {
        dialogElement.removeEventListener('toggle', handlePopoverClose)
      }
    }
  }, [closePopout, handleDocumentClick, handleDocumentMouseDown])

  useEscapeKey(isPopoutOpen, closePopout)

  // Prevent body scrolling when popout is open while keeping scrollbar visible
  useEffect(() => {
    if (isPopoutOpen) {
      // Store the current scroll position and styles
      const scrollY = window.scrollY
      const originalPosition = document.body.style.position
      const originalTop = document.body.style.top
      const originalWidth = document.body.style.width
      const originalOverflow = document.body.style.overflow

      // Fix the body position to prevent scrolling while keeping scrollbar
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        // Restore the original styles and scroll position
        document.body.style.position = originalPosition
        document.body.style.top = originalTop
        document.body.style.width = originalWidth
        document.body.style.overflow = originalOverflow

        // Restore scroll position
        window.scrollTo(0, scrollY)
      }
    }
  }, [isPopoutOpen])

  // Handle tag creation
  const handleTagCreated = () => {
    // Return to the main tag list view
    setCurrentView(TagsPopoutView.LIST)
  }

  // Handle tag update
  const handleTagUpdated = () => {
    // Return to the main tag list view
    setCurrentView(TagsPopoutView.LIST)
    setEditingTag(null)
  }

  // Handle tag deletion
  const handleTagDeleted = () => {
    // Return to the main tag list view
    setCurrentView(TagsPopoutView.LIST)
    setEditingTag(null)
  }

  // Handle view switching
  const handleSwitchToCreateView = () => {
    setCurrentView(TagsPopoutView.CREATE)
  }

  const handleSwitchToEditView = (tag: AssetTag) => {
    setEditingTag(tag)
    setCurrentView(TagsPopoutView.EDIT)
  }

  const handleBackToList = () => {
    setCurrentView(TagsPopoutView.LIST)
    setEditingTag(null)
  }

  // Handle modal opening
  const handleOpenModal = () => {
    closePopout()
    openModal(ModalOpenVariant.ASSET)
  }

  // Handle popout closing
  const handleClose = () => {
    if (dialogRef.current) {
      dialogRef.current.hidePopover()
    }
    closePopout()
  }

  // When popout closes, reset to list view
  useEffect(() => {
    if (!isPopoutOpen) {
      setCurrentView(TagsPopoutView.LIST)
      setSearchTerm('')
      setEditingTag(null)
    }
  }, [isPopoutOpen])

  // If not open, don't render content
  if (!isPopoutOpen) {
    return null
  }

  return (
    <dialog ref={dialogRef} className={styles.popoutContainer} popover="auto">
      <TooltipContainerProvider container={dialogRef.current}>
        {currentView === TagsPopoutView.CREATE ? (
          <CreateTagScreen
            onBack={handleBackToList}
            onTagCreated={handleTagCreated}
            assetId={activeAssetData?.id}
          />
        ) : currentView === TagsPopoutView.EDIT && editingTag ? (
          <EditTagScreen
            tag={editingTag}
            onBack={handleBackToList}
            onTagUpdated={handleTagUpdated}
            onTagDeleted={handleTagDeleted}
          />
        ) : (
          <TagsListView
            searchFilteredTags={searchFilteredTags}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            currentAssetTags={activeAssetData?.tags || []}
            isTagsUpdating={isTagsUpdating}
            onCreateNewTag={handleSwitchToCreateView}
            onEditTag={handleSwitchToEditView}
            onOpenModal={handleOpenModal}
            onClose={handleClose}
          />
        )}
      </TooltipContainerProvider>
    </dialog>
  )
}
